import { Component, ElementRef, Input, OnInit, ViewChild } from "@angular/core";
import { FileCollection, LoggingService, LogLevel, UploadService } from "@merri/common";
import { catchError, from, mergeMap, of, tap } from "rxjs";
import { FileInfo } from "src/app/core/models/file-to-upload";
import { UploadProgress } from "src/app/core/models/upload-progress";
import { UploadStatus } from "src/app/core/models/upload-status";

const UPLOAD_CHUNK_SIZE_BYTES: number = 104857600;

interface AssetUploadResponse {
  fileErrors: {
    fileName: string;
    error: string;
  }[];
  error: string;
  successfulFiles: string[];
  timestamp: Date;
  totalFiles: number;
  totalSize: number;
}

@Component({
  selector: "app-asset-upload",
  templateUrl: "./asset-upload.component.html",
  styleUrls: ["./asset-upload.component.scss"],
  standalone: false,
})
export class AssetUploadComponent implements OnInit {
  selectedFile: FileInfo;
  uploading: boolean;
  uploadResponses: Partial<AssetUploadResponse>[] = [];
  spinnerMode: string;
  uploadStatus: string;
  spinnerValue: number;
  totalUploadSize: number;

  @Input() url: string;
  @Input() assetBucketName: string = "files";

  @ViewChild("assetFilesUploadInput", { static: true }) assetFilesUploadInput: ElementRef;

  constructor(private _uploadService: UploadService, private _loggingService: LoggingService) {}

  ngOnInit(): void {}

  uploadAssetFiles() {
    this.uploading = true;
    this.uploadStatus = null;
    this.spinnerMode = "determinate";
    this.spinnerValue = 0;

    const input = this.assetFilesUploadInput.nativeElement as HTMLInputElement;
    const files = input.files;
    const currentFileNames: string[] = [];

    if (files) {
      const filesArray: File[][] = [];
      let currentChunkSize: number = 0;
      let currentFileArray = [];

      for (let i = 0; i < files.length; i++) {
        if (currentChunkSize + files[i].size < UPLOAD_CHUNK_SIZE_BYTES) {
          currentFileArray.push(files[i]);
          currentChunkSize += files[i].size;
        } else {
          filesArray.push(currentFileArray.slice());
          currentFileArray = [files[i]];
          currentChunkSize = files[i].size;
        }

        currentFileNames.push(files[i].name);

        if (i === files.length - 1 && currentFileArray.length) {
          filesArray.push(currentFileArray);
        }
      }

      const fileErrors: {
        fileName: string;
        error: string;
      }[] = [];

      from(filesArray)
        .pipe(
          mergeMap(files => {
            const fileCollection: FileCollection = {};
            fileCollection[this.assetBucketName] = [];

            for (let i = 0; i < files.length; i++) {
              fileCollection[this.assetBucketName].push(files[i]);
            }

            return this._uploadService.uploadFiles$(fileCollection, this.url).pipe(
              tap((progress: UploadProgress) => {
                if (progress.status == UploadStatus.Progress) {
                  if (progress.progress === 100) {
                    this.spinnerMode = "indeterminate";
                    this.uploadStatus = "Processing...";
                  } else {
                    this.spinnerMode = "determinate";
                    this.uploadStatus = "Uploading...";
                  }
                  this.spinnerValue = progress.progress;
                } else if (progress.status == UploadStatus.Complete) {
                  const assetUploadCompleteResponse: AssetUploadResponse = progress.response;

                  if (assetUploadCompleteResponse.fileErrors.length) {
                    fileErrors.push(...assetUploadCompleteResponse.fileErrors);
                  }
                }
              }),
              catchError(err => {
                this.uploading = false;
                this.selectedFile = null;

                this._loggingService.logRemote(
                  LogLevel.Warning,
                  "AssetUploadComponent - uploadAssetFiles - error",
                  "AssetUploadComponent.uploadAssetFiles",
                  {},
                  err,
                );

                if (err.status === 400) {
                  const assetUploadErrorResponse: AssetUploadResponse = err.error;
                  assetUploadErrorResponse.timestamp = new Date();
                  assetUploadErrorResponse.totalFiles = currentFileNames.length;

                  if (assetUploadErrorResponse.fileErrors.length) {
                    fileErrors.push(...assetUploadErrorResponse.fileErrors);
                  }

                  this._addUploadResponse(assetUploadErrorResponse);
                } else {
                  const fe = currentFileNames.map(fileName => {
                    return {
                      fileName: fileName,
                      error: null,
                    };
                  });

                  fileErrors.push(...fe);

                  this._addUploadResponse({
                    error: "Unknown Error",
                    timestamp: new Date(),
                    totalFiles: currentFileNames.length,
                    totalSize: this.totalUploadSize,
                    fileErrors: fe,
                  });
                }

                return of(null);
              }),
            );
          }, 5),
        )
        .subscribe({
          complete: () => {
            this.uploading = false;
            this.selectedFile = null;

            const s = currentFileNames.filter(file => !fileErrors.some(fileError => fileError.fileName === file));

            if (s.length) {
              this._addUploadResponse({
                timestamp: new Date(),
                totalFiles: currentFileNames.length,
                totalSize: this.totalUploadSize,
                successfulFiles: s,
                fileErrors: fileErrors,
              });
            }
          },
        });
    }
  }

  onFilesSelected(): void {
    const input = this.assetFilesUploadInput.nativeElement as HTMLInputElement;
    const files = input.files;

    if (files) {
      let totalSize = 0;
      for (let i = 0; i < files.length; i++) {
        totalSize += files[i].size;
      }

      this.selectedFile = {
        name: `${files.length} file(s) selected.`,
        size: totalSize,
        lastModified: null,
      };

      this.totalUploadSize = totalSize;
    }
  }

  private _addUploadResponse(uploadResponse: Partial<AssetUploadResponse>): void {
    this.uploadResponses.unshift(uploadResponse);
  }
}
